import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Activity,
    Database,
    Shield,
    CheckCircle,
    Settings,
    Zap,
    Code,
    Gauge,
    Search,
    Filter,
    Calendar,
    Building,
    MessageSquare,
    User,
    Bell,
    LogOut,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { SystemTriageTab } from './tabs/SystemTriageTab';
import { PrecisionDataTab } from './tabs/PrecisionDataTab';
import { HealthAnalysisTab } from './tabs/HealthAnalysisTab';
import { QADashboardTab } from './tabs/QADashboardTab';
import { ConversionOpsTab } from './tabs/ConversionOpsTab';
import { CodeOpsTab } from './tabs/CodeOpsTab';
import { OperationsControlTab } from './tabs/OperationsControlTab';
import { AppDockTab } from './tabs/AppDockTab';
import { NativeCommsTab } from './tabs/NativeCommsTab';
import { PipelineConfigurationTab } from './tabs/PipelineConfigurationTab';
import { useEngineeringAuth } from '../../hooks/useEngineeringAuth';
import { EngineeringAuth } from './auth/EngineeringAuth';
import { EngineeringProfileService } from '../../services/EngineeringProfileService';
import {
    EngineeringBridgeProvider,
    useEngineeringBridgeContext,
} from './context/EngineeringBridgeContext';

const DemoModeToggle: React.FC = () => {
    const { isDemo, toggleMode } = useEngineeringBridgeContext();

    return (
        <button
            onClick={toggleMode}
            className={`mt-4 px-3 py-2 rounded-xl transition-all duration-200 hover:scale-105 cursor-pointer ${
                isDemo
                    ? 'bg-yellow-500/15 border border-yellow-500/25 hover:bg-yellow-500/25'
                    : 'bg-green-500/15 border border-green-500/25 hover:bg-green-500/25'
            }`}
            title={`Click to switch to ${isDemo ? 'Live' : 'Demo'} mode`}
        >
            <div className="flex items-center space-x-2">
                <div
                    className={`w-2 h-2 rounded-full animate-pulse ${
                        isDemo ? 'bg-yellow-400' : 'bg-green-400'
                    }`}
                ></div>
                <span
                    className={`text-xs font-medium ${
                        isDemo ? 'text-yellow-300' : 'text-green-300'
                    }`}
                >
                    {isDemo ? 'Demo Mode' : 'Live Data'}
                </span>
            </div>
        </button>
    );
};

const navigationTabs = [
    {
        id: 'system-triage',
        label: 'System Triage & Health',
        icon: Activity,
        color: 'from-red-500 to-orange-500',
    },
    {
        id: 'native-comms',
        label: 'Native Communications',
        icon: MessageSquare,
        color: 'from-blue-500 to-indigo-500',
    },
    {
        id: 'precision-data',
        label: 'Precision Data',
        icon: Database,
        color: 'from-blue-500 to-cyan-500',
    },
    {
        id: 'health-analysis',
        label: 'Health Analysis',
        icon: Shield,
        color: 'from-green-500 to-emerald-500',
    },
    {
        id: 'qa-dashboard',
        label: 'QA Dashboard',
        icon: CheckCircle,
        color: 'from-purple-500 to-pink-500',
    },
    {
        id: 'conversion-ops',
        label: 'ConversionOps',
        icon: Zap,
        color: 'from-yellow-500 to-orange-500',
    },
    {
        id: 'pipeline-config',
        label: 'Pipeline Configuration',
        icon: Settings,
        color: 'from-slate-500 to-gray-500',
    },
    { id: 'code-ops', label: 'CodeOps', icon: Code, color: 'from-indigo-500 to-purple-500' },
    {
        id: 'operations-control',
        label: 'Operations Control',
        icon: Settings,
        color: 'from-slate-500 to-gray-500',
    },
    { id: 'app-dock', label: 'App Dock', icon: Building, color: 'from-teal-500 to-blue-500' },
];

export const EngineeringBridgeShell: React.FC = () => {
    const { user, isAuthenticated, logout, mode, switchToDemo, switchToLive, updateProfile } =
        useEngineeringAuth();
    const [activeTab, setActiveTab] = useState('system-triage');
    const [showAuth, setShowAuth] = useState(!isAuthenticated);
    const [dateRange, setDateRange] = useState('24h');
    const [tenant, setTenant] = useState('all');
    const [environment, setEnvironment] = useState('production');
    const [service, setService] = useState('all');
    const [stage, setStage] = useState('all');
    const [showNotifications, setShowNotifications] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [notificationCount, setNotificationCount] = useState(3);
    const [refreshInterval, setRefreshInterval] = useState('30');
    const [dataSource, setDataSource] = useState('live');
    const [enableNotifications, setEnableNotifications] = useState(true);
    const [enableAutoRefresh, setEnableAutoRefresh] = useState(true);
    const [enableDarkMode, setEnableDarkMode] = useState(true);
    const [profileData, setProfileData] = useState({
        name: '',
        email: '',
        phoneNumber: '',
        notifications: {
            email: true,
            sms: false,
            slack: true,
            inApp: true,
        },
    });

    const profileService = EngineeringProfileService.getInstance();

    useEffect(() => {
        setShowAuth(!isAuthenticated);
    }, [isAuthenticated]);

    useEffect(() => {
        if (user) {
            setProfileData({
                name: user.name || '',
                email: user.email || '',
                phoneNumber: (user as any).phoneNumber || '',
                notifications: {
                    email: (user.preferences?.notifications as any)?.email ?? true,
                    sms: (user.preferences?.notifications as any)?.sms ?? false,
                    slack: (user.preferences?.notifications as any)?.slack ?? true,
                    inApp: (user.preferences?.notifications as any)?.inApp ?? true,
                },
            });
        }
    }, [user]);

    useEffect(() => {
        if (enableAutoRefresh) {
            const interval = setInterval(
                () => {
                    console.log('Auto-refreshing data...');
                },
                parseInt(refreshInterval) * 1000
            );

            return () => clearInterval(interval);
        }
    }, [enableAutoRefresh, refreshInterval]);

    const handleAuthSuccess = () => {
        setShowAuth(false);
    };

    const handleLogout = () => {
        logout();
        setShowAuth(true);
    };

    const handleDemoToggle = () => {
        if (mode === 'demo') {
            switchToLive();
        } else {
            switchToDemo();
        }
        toggleMode();
    };

    const handleProfileUpdate = async () => {
        if (user) {
            try {
                await updateProfile({
                    name: profileData.name,
                    preferences: {
                        ...user.preferences,
                        notifications: profileData.notifications,
                    },
                } as any);
                setShowSettings(false);
            } catch (error) {
                console.error('Failed to update profile:', error);
            }
        }
    };

    if (showAuth) {
        return <EngineeringAuth onAuthSuccess={handleAuthSuccess} />;
    }

    const renderActiveTab = () => {
        switch (activeTab) {
            case 'system-triage':
                return <SystemTriageTab />;
            case 'native-comms':
                return <NativeCommsTab />;
            case 'precision-data':
                return <PrecisionDataTab />;
            case 'health-analysis':
                return <HealthAnalysisTab />;
            case 'qa-dashboard':
                return <QADashboardTab />;
            case 'conversion-ops':
                return <ConversionOpsTab />;
            case 'pipeline-config':
                return <PipelineConfigurationTab />;
            case 'code-ops':
                return <CodeOpsTab />;
            case 'operations-control':
                return <OperationsControlTab />;
            case 'app-dock':
                return <AppDockTab />;
            default:
                return <SystemTriageTab />;
        }
    };

    return (
        <EngineeringBridgeProvider initialIsDemo={mode === 'demo'}>
            <div className="flex h-screen sphix-background">
                {/* Sidebar Navigation */}
                <motion.div
                    className="evolut-sidebar flex flex-col sphix-sidebar fixed left-0 top-0 h-screen z-40"
                    style={{ width: '280px' }}
                    initial={{ x: -280 }}
                    animate={{ x: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                >
                    {/* Sidebar Header */}
                    <div className="p-6 border-b border-slate-700/15">
                        <div>
                            <h2 className="text-2xl font-bold text-white">Engineering Bridge</h2>
                            <p className="text-xs text-slate-300 opacity-75 font-medium">
                                Conversion Quality & Operations
                            </p>
                        </div>

                        {/* Demo Mode Toggle */}
                        <DemoModeToggle />
                    </div>

                    {/* Navigation Tabs */}
                    <div className="flex-1 overflow-y-auto py-4">
                        <nav className="space-y-1 px-4">
                            {navigationTabs.map(tab => (
                                <motion.button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-left transition-all duration-200 ${
                                        activeTab === tab.id
                                            ? 'bg-gradient-to-r ' +
                                              tab.color +
                                              ' text-white shadow-lg shadow-purple-500/30'
                                            : 'text-slate-300 hover:bg-slate-800/20 hover:text-white'
                                    }`}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <tab.icon size={18} />
                                    <span className="font-semibold text-sm">{tab.label}</span>
                                </motion.button>
                            ))}
                        </nav>
                    </div>
                </motion.div>

                {/* Main Content */}
                <div className="flex-1 ml-[280px] flex flex-col h-screen">
                    {/* Header */}
                    <header
                        className="backdrop-blur-sm border-b border-slate-700/15 p-6"
                        style={{
                            background:
                                'linear-gradient(135deg, hsl(280, 30%, 18%) 0%, hsl(280, 25%, 15%) 100%)',
                        }}
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="text-3xl font-bold text-white">
                                    {navigationTabs.find(tab => tab.id === activeTab)?.label}
                                </h1>
                                <p className="text-sm text-slate-300 mt-1 opacity-80">
                                    {mode === 'demo' ? 'Demo data - ' : ''}Real-time engineering
                                    metrics and operations
                                </p>
                            </div>

                            {/* Right Side Icons */}
                            <div className="flex items-center space-x-4">
                                <div className="flex items-center gap-2 mr-4">
                                    <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center">
                                        <span className="text-white text-sm font-semibold">
                                            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                                        </span>
                                    </div>
                                    <div className="text-sm">
                                        <div className="text-white font-medium">
                                            {user?.name || 'User'}
                                        </div>
                                        <div className="text-slate-400 text-xs">
                                            {(user as any)?.role || user?.workspaceId || 'user'}
                                        </div>
                                    </div>
                                </div>

                                <div className="relative">
                                    <button
                                        onClick={() => setShowNotifications(!showNotifications)}
                                        className="relative p-2 rounded-xl bg-slate-800/30 hover:bg-slate-700/50 transition-all duration-200 hover:scale-105"
                                    >
                                        <Bell
                                            size={20}
                                            className="text-slate-300 hover:text-white"
                                        />
                                        {notificationCount > 0 && (
                                            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                                                {notificationCount}
                                            </span>
                                        )}
                                    </button>

                                    {showNotifications && (
                                        <div className="absolute right-0 top-12 w-80 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl z-50">
                                            <div className="p-4 border-b border-slate-700">
                                                <h3 className="text-white font-semibold">
                                                    Notifications
                                                </h3>
                                            </div>
                                            <div className="max-h-96 overflow-y-auto">
                                                <div className="p-3 border-b border-slate-800 hover:bg-slate-800/50">
                                                    <div className="flex items-start space-x-3">
                                                        <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                                                        <div>
                                                            <p className="text-white text-sm">
                                                                System Alert: High error rate
                                                                detected
                                                            </p>
                                                            <p className="text-slate-400 text-xs">
                                                                2 minutes ago
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="p-3 border-b border-slate-800 hover:bg-slate-800/50">
                                                    <div className="flex items-start space-x-3">
                                                        <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
                                                        <div>
                                                            <p className="text-white text-sm">
                                                                Queue depth warning:
                                                                conversion-queue
                                                            </p>
                                                            <p className="text-slate-400 text-xs">
                                                                5 minutes ago
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="p-3 hover:bg-slate-800/50">
                                                    <div className="flex items-start space-x-3">
                                                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                                                        <div>
                                                            <p className="text-white text-sm">
                                                                Deployment completed successfully
                                                            </p>
                                                            <p className="text-slate-400 text-xs">
                                                                10 minutes ago
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="p-3 border-t border-slate-700">
                                                <button className="text-blue-400 hover:text-blue-300 text-sm">
                                                    View all notifications
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <button
                                    onClick={() => setShowSettings(!showSettings)}
                                    className="p-2 rounded-xl bg-slate-800/30 hover:bg-slate-700/50 transition-all duration-200 hover:scale-105"
                                >
                                    <Settings
                                        size={20}
                                        className="text-slate-300 hover:text-white"
                                    />
                                </button>

                                <div className="relative">
                                    <button
                                        onClick={() => setShowUserMenu(!showUserMenu)}
                                        className="p-2 rounded-xl bg-slate-800/30 hover:bg-slate-700/50 transition-all duration-200 hover:scale-105"
                                    >
                                        <User
                                            size={20}
                                            className="text-slate-300 hover:text-white"
                                        />
                                    </button>

                                    {showUserMenu && (
                                        <div className="absolute right-0 top-12 w-64 sphix-card-enhanced border border-slate-700 rounded-lg shadow-xl z-50">
                                            <div className="p-4">
                                                <div className="text-white font-medium mb-2">
                                                    User Menu
                                                </div>
                                                <div className="mb-3 pb-3 border-b border-slate-700">
                                                    <div className="text-sm text-slate-300">
                                                        {user?.email}
                                                    </div>
                                                    <div className="text-xs text-slate-500">
                                                        {user?.workspaceId}
                                                    </div>
                                                </div>
                                                <div className="space-y-2">
                                                    <button
                                                        className="w-full text-left px-3 py-2 text-slate-300 hover:bg-slate-800/50 hover:text-white rounded"
                                                        onClick={() => {
                                                            setShowUserMenu(false);
                                                            setShowSettings(true);
                                                        }}
                                                    >
                                                        Profile Settings
                                                    </button>
                                                    <button
                                                        className="w-full text-left px-3 py-2 text-slate-300 hover:bg-slate-800/50 hover:text-white rounded"
                                                        onClick={handleDemoToggle}
                                                    >
                                                        Switch to{' '}
                                                        {mode === 'demo' ? 'Live' : 'Demo'} Mode
                                                    </button>
                                                    <button
                                                        className="w-full text-left px-3 py-2 text-red-400 hover:bg-slate-800/50 hover:text-red-300 rounded flex items-center"
                                                        onClick={handleLogout}
                                                    >
                                                        <LogOut className="w-4 h-4 mr-2" />
                                                        Sign Out
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Filter Bar */}
                        <div className="mt-6 flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                                <Calendar size={16} className="text-slate-400" />
                                <select
                                    value={dateRange}
                                    onChange={e => setDateRange(e.target.value)}
                                    className="bg-slate-800/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3439fa]"
                                >
                                    <option value="5m">Last 5 minutes</option>
                                    <option value="1h">Last hour</option>
                                    <option value="24h">Last 24 hours</option>
                                    <option value="7d">Last 7 days</option>
                                    <option value="30d">Last 30 days</option>
                                </select>
                            </div>

                            <div className="flex items-center space-x-2">
                                <Building size={16} className="text-slate-400" />
                                <select
                                    value={tenant}
                                    onChange={e => setTenant(e.target.value)}
                                    className="bg-slate-800/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3439fa]"
                                >
                                    <option value="all">All Tenants</option>
                                    <option value="tyler">Tyler</option>
                                    <option value="hivecore">HiveCore</option>
                                    <option value="beehive">Beehive</option>
                                </select>
                            </div>

                            <div className="flex items-center space-x-2">
                                <Gauge size={16} className="text-slate-400" />
                                <select
                                    value={environment}
                                    onChange={e => setEnvironment(e.target.value)}
                                    className="bg-slate-800/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3439fa]"
                                >
                                    <option value="production">Production</option>
                                    <option value="staging">Staging</option>
                                    <option value="development">Development</option>
                                </select>
                            </div>

                            {activeTab === 'code-ops' && (
                                <div className="flex items-center space-x-2">
                                    <Code size={16} className="text-slate-400" />
                                    <select
                                        value={service}
                                        onChange={e => setService(e.target.value)}
                                        className="bg-slate-800/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3439fa]"
                                    >
                                        <option value="all">All Services</option>
                                        <option value="api">API</option>
                                        <option value="workers">Workers</option>
                                        <option value="frontend">Frontend</option>
                                    </select>
                                </div>
                            )}

                            {activeTab === 'conversion-ops' && (
                                <div className="flex items-center space-x-2">
                                    <Zap size={16} className="text-slate-400" />
                                    <select
                                        value={stage}
                                        onChange={e => setStage(e.target.value)}
                                        className="bg-slate-800/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3439fa]"
                                    >
                                        <option value="all">All Stages</option>
                                        <option value="extract">Extract</option>
                                        <option value="transform">Transform</option>
                                        <option value="load">Load</option>
                                    </select>
                                </div>
                            )}
                        </div>
                    </header>

                    {/* Tab Content */}
                    <main className="flex-1 overflow-y-auto p-6 sphix-scrollbar h-0">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeTab}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.3, ease: 'easeInOut' }}
                                className="min-h-full"
                            >
                                {renderActiveTab()}
                            </motion.div>
                        </AnimatePresence>
                    </main>

                    {/* Settings Modal */}
                    {showSettings && (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                            <motion.div
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                className="w-full max-w-2xl max-h-[90vh] overflow-y-auto"
                            >
                                <Card className="sphix-card-enhanced">
                                    <CardContent className="p-6">
                                        <div className="flex items-center justify-between mb-6">
                                            <h2 className="text-xl font-semibold text-white">
                                                Engineering Bridge Settings
                                            </h2>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setShowSettings(false)}
                                                className="text-slate-400 hover:text-white"
                                            >
                                                ×
                                            </Button>
                                        </div>

                                        <Tabs defaultValue="profile" className="space-y-6">
                                            <div className="border-b border-slate-700">
                                                <nav className="flex space-x-8">
                                                    <button className="py-2 px-1 border-b-2 border-purple-600 text-purple-400 font-medium text-sm">
                                                        Profile
                                                    </button>
                                                    <button className="py-2 px-1 border-b-2 border-transparent text-slate-400 hover:text-slate-300 font-medium text-sm">
                                                        System
                                                    </button>
                                                </nav>
                                            </div>

                                            <div className="space-y-6">
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <Label className="text-slate-300 text-sm font-medium">
                                                            Full Name
                                                        </Label>
                                                        <Input
                                                            value={profileData.name}
                                                            onChange={e =>
                                                                setProfileData({
                                                                    ...profileData,
                                                                    name: e.target.value,
                                                                })
                                                            }
                                                            className="bg-slate-800 border-slate-600 text-black mt-2"
                                                            placeholder="Your full name"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label className="text-slate-300 text-sm font-medium">
                                                            Email
                                                        </Label>
                                                        <Input
                                                            value={profileData.email}
                                                            onChange={e =>
                                                                setProfileData({
                                                                    ...profileData,
                                                                    email: e.target.value,
                                                                })
                                                            }
                                                            className="bg-slate-800 border-slate-600 text-black mt-2"
                                                            placeholder="your.email@company.com"
                                                            disabled={mode === 'live'}
                                                        />
                                                    </div>
                                                </div>

                                                <div>
                                                    <Label className="text-slate-300 text-sm font-medium">
                                                        Phone Number
                                                    </Label>
                                                    <Input
                                                        value={profileData.phoneNumber}
                                                        onChange={e =>
                                                            setProfileData({
                                                                ...profileData,
                                                                phoneNumber: e.target.value,
                                                            })
                                                        }
                                                        className="bg-slate-800 border-slate-600 text-black mt-2"
                                                        placeholder="+1 (555) 123-4567"
                                                    />
                                                </div>

                                                <div>
                                                    <Label className="text-slate-300 text-sm font-medium mb-3 block">
                                                        Notification Preferences
                                                    </Label>
                                                    <div className="space-y-3">
                                                        <div className="flex items-center space-x-2">
                                                            <Checkbox
                                                                id="emailNotif"
                                                                checked={
                                                                    profileData.notifications.email
                                                                }
                                                                onChange={e =>
                                                                    setProfileData({
                                                                        ...profileData,
                                                                        notifications: {
                                                                            ...profileData.notifications,
                                                                            email: e.target.checked,
                                                                        },
                                                                    })
                                                                }
                                                            />
                                                            <Label
                                                                htmlFor="emailNotif"
                                                                className="text-slate-300 text-sm"
                                                            >
                                                                Email notifications
                                                            </Label>
                                                        </div>
                                                        <div className="flex items-center space-x-2">
                                                            <Checkbox
                                                                id="smsNotif"
                                                                checked={
                                                                    profileData.notifications.sms
                                                                }
                                                                onChange={e =>
                                                                    setProfileData({
                                                                        ...profileData,
                                                                        notifications: {
                                                                            ...profileData.notifications,
                                                                            sms: e.target.checked,
                                                                        },
                                                                    })
                                                                }
                                                            />
                                                            <Label
                                                                htmlFor="smsNotif"
                                                                className="text-slate-300 text-sm"
                                                            >
                                                                SMS notifications
                                                            </Label>
                                                        </div>
                                                        <div className="flex items-center space-x-2">
                                                            <Checkbox
                                                                id="slackNotif"
                                                                checked={
                                                                    profileData.notifications.slack
                                                                }
                                                                onChange={e =>
                                                                    setProfileData({
                                                                        ...profileData,
                                                                        notifications: {
                                                                            ...profileData.notifications,
                                                                            slack: e.target.checked,
                                                                        },
                                                                    })
                                                                }
                                                            />
                                                            <Label
                                                                htmlFor="slackNotif"
                                                                className="text-slate-300 text-sm"
                                                            >
                                                                Slack notifications
                                                            </Label>
                                                        </div>
                                                        <div className="flex items-center space-x-2">
                                                            <Checkbox
                                                                id="inAppNotif"
                                                                checked={
                                                                    profileData.notifications.inApp
                                                                }
                                                                onChange={e =>
                                                                    setProfileData({
                                                                        ...profileData,
                                                                        notifications: {
                                                                            ...profileData.notifications,
                                                                            inApp: e.target.checked,
                                                                        },
                                                                    })
                                                                }
                                                            />
                                                            <Label
                                                                htmlFor="inAppNotif"
                                                                className="text-slate-300 text-sm"
                                                            >
                                                                In-app notifications
                                                            </Label>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="border-t border-slate-700 pt-6">
                                                    <Label className="text-slate-300 text-sm font-medium mb-3 block">
                                                        System Settings
                                                    </Label>
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div>
                                                            <Label className="text-slate-300 text-sm">
                                                                Data Source
                                                            </Label>
                                                            <Select
                                                                value={dataSource}
                                                                onValueChange={setDataSource}
                                                            >
                                                                <SelectTrigger className="bg-slate-800 border-slate-600 text-white mt-2">
                                                                    <SelectValue />
                                                                </SelectTrigger>
                                                                <SelectContent className="bg-slate-800 border-slate-600">
                                                                    <SelectItem value="live">
                                                                        Live Data
                                                                    </SelectItem>
                                                                    <SelectItem value="demo">
                                                                        Demo Data
                                                                    </SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                        </div>

                                                        <div>
                                                            <Label className="text-slate-300 text-sm">
                                                                Refresh Interval
                                                            </Label>
                                                            <Select
                                                                value={refreshInterval}
                                                                onValueChange={setRefreshInterval}
                                                            >
                                                                <SelectTrigger className="bg-slate-800 border-slate-600 text-white mt-2">
                                                                    <SelectValue />
                                                                </SelectTrigger>
                                                                <SelectContent className="bg-slate-800 border-slate-600">
                                                                    <SelectItem value="15">
                                                                        15 seconds
                                                                    </SelectItem>
                                                                    <SelectItem value="30">
                                                                        30 seconds
                                                                    </SelectItem>
                                                                    <SelectItem value="60">
                                                                        1 minute
                                                                    </SelectItem>
                                                                    <SelectItem value="300">
                                                                        5 minutes
                                                                    </SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                    </div>

                                                    <div className="space-y-3 mt-4">
                                                        <div className="flex items-center space-x-2">
                                                            <Checkbox
                                                                id="autoRefresh"
                                                                checked={enableAutoRefresh}
                                                                onChange={e =>
                                                                    setEnableAutoRefresh(
                                                                        e.target.checked
                                                                    )
                                                                }
                                                            />
                                                            <Label
                                                                htmlFor="autoRefresh"
                                                                className="text-slate-300 text-sm"
                                                            >
                                                                Auto Refresh Data
                                                            </Label>
                                                        </div>

                                                        <div className="flex items-center space-x-2">
                                                            <Checkbox
                                                                id="darkMode"
                                                                checked={enableDarkMode}
                                                                onChange={e =>
                                                                    setEnableDarkMode(
                                                                        e.target.checked
                                                                    )
                                                                }
                                                            />
                                                            <Label
                                                                htmlFor="darkMode"
                                                                className="text-slate-300 text-sm"
                                                            >
                                                                Dark Mode
                                                            </Label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex justify-end gap-3 pt-6 border-t border-slate-700">
                                                <Button
                                                    variant="outline"
                                                    onClick={() => setShowSettings(false)}
                                                    className="border-slate-600 text-slate-300 hover:bg-slate-700"
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    onClick={handleProfileUpdate}
                                                    className="bg-purple-600 hover:bg-purple-700 text-white"
                                                >
                                                    Save Changes
                                                </Button>
                                            </div>
                                        </Tabs>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        </div>
                    )}

                    {/* Click outside handlers */}
                    {(showNotifications || showUserMenu) && (
                        <div
                            className="fixed inset-0 z-40"
                            onClick={() => {
                                setShowNotifications(false);
                                setShowUserMenu(false);
                            }}
                        />
                    )}
                </div>
            </div>
        </EngineeringBridgeProvider>
    );
};
